import React, { useState, useEffect } from 'react';
import './Settings.css';

const Settings = () => {
  const [qbStatus, setQbStatus] = useState({ connected: false, loading: true });
  const [gmailStatus, setGmailStatus] = useState({ connected: false, loading: true });
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [homeAddress, setHomeAddress] = useState({
    address: '',
    city: '',
    state: 'TX',
    zip: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);

  useEffect(() => {
    checkQuickBooksStatus();
    checkGmailStatus();
    fetchHomeAddress();

    // Check for callback messages in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('qb_connected') === 'true') {
      setMessage({ type: 'success', text: 'Successfully connected to QuickBooks!' });
      window.history.replaceState({}, '', '/settings');
    } else if (params.get('qb_error')) {
      setMessage({ type: 'error', text: `QuickBooks error: ${params.get('qb_error')}` });
      window.history.replaceState({}, '', '/settings');
    } else if (params.get('gmail_connected') === 'true') {
      setMessage({ type: 'success', text: 'Successfully connected to Gmail!' });
      window.history.replaceState({}, '', '/settings');
    } else if (params.get('gmail_error')) {
      setMessage({ type: 'error', text: `Gmail error: ${params.get('gmail_error')}` });
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const checkQuickBooksStatus = async () => {
    try {
      const response = await fetch('/api/quickbooks/status');
      const data = await response.json();
      setQbStatus({ ...data, loading: false });
    } catch (error) {
      console.error('Error checking QuickBooks status:', error);
      setQbStatus({ connected: false, loading: false });
    }
  };

  const checkGmailStatus = async () => {
    try {
      const response = await fetch('/api/gmail/status');
      const data = await response.json();
      setGmailStatus({ ...data, loading: false });
    } catch (error) {
      console.error('Error checking Gmail status:', error);
      setGmailStatus({ connected: false, loading: false });
    }
  };

  const fetchHomeAddress = async () => {
    try {
      const response = await fetch('/api/mileage/home-address');
      const data = await response.json();
      if (data.address) {
        setHomeAddress(data.address);
      }
    } catch (error) {
      console.error('Error fetching home address:', error);
    }
  };

  const handleSaveHomeAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);

    try {
      const response = await fetch('/api/mileage/home-address', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(homeAddress)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Home address saved!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save address' });
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setMessage({ type: 'error', text: 'Failed to save address' });
    } finally {
      setSavingAddress(false);
    }
  };

  const handleConnectQuickBooks = async () => {
    try {
      const response = await fetch('/api/quickbooks/connect');
      const data = await response.json();
      if (data.authUri) {
        window.location.href = data.authUri;
      }
    } catch (error) {
      console.error('Error connecting to QuickBooks:', error);
      setMessage({ type: 'error', text: 'Failed to start QuickBooks connection' });
    }
  };

  const handleDisconnectQuickBooks = async () => {
    if (!window.confirm('Are you sure you want to disconnect QuickBooks?')) {
      return;
    }

    try {
      const response = await fetch('/api/quickbooks/disconnect', { method: 'POST' });
      if (response.ok) {
        setQbStatus({ connected: false, loading: false });
        setMessage({ type: 'success', text: 'Disconnected from QuickBooks' });
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect from QuickBooks' });
    }
  };

  const handleConnectGmail = async () => {
    try {
      const response = await fetch('/api/gmail/connect');
      const data = await response.json();
      if (data.authUri) {
        window.location.href = data.authUri;
      }
    } catch (error) {
      console.error('Error connecting to Gmail:', error);
      setMessage({ type: 'error', text: 'Failed to start Gmail connection' });
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Gmail? You will not be able to send emails until you reconnect.')) {
      return;
    }

    try {
      const response = await fetch('/api/gmail/disconnect', { method: 'POST' });
      if (response.ok) {
        setGmailStatus({ connected: false, loading: false });
        setMessage({ type: 'success', text: 'Disconnected from Gmail' });
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect from Gmail' });
    }
  };

  const handleSyncAllCustomers = async () => {
    setSyncing(true);
    setMessage(null);

    try {
      const response = await fetch('/api/quickbooks/sync/customers', { method: 'POST' });
      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: 'success',
          text: `Synced ${data.success} customers to QuickBooks. ${data.failed > 0 ? `${data.failed} failed.` : ''}`
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Sync failed' });
      }
    } catch (error) {
      console.error('Error syncing:', error);
      setMessage({ type: 'error', text: 'Failed to sync customers' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1 className="page-header__title">Settings</h1>
      </div>

      {message && (
        <div className={`alert alert--${message.type}`}>
          {message.text}
          <button className="alert__close" onClick={() => setMessage(null)}>&times;</button>
        </div>
      )}

      {/* Home Address Section */}
      <div className="settings-section">
        <div className="settings-section__header">
          <h2>Home/Office Address</h2>
          <p className="settings-section__desc">
            Set your home or office address for mileage tracking. This is used as the starting point for calculating trip distances.
          </p>
        </div>

        <div className="settings-card">
          <form onSubmit={handleSaveHomeAddress}>
            <div className="form-group">
              <label className="form-label">Street Address</label>
              <input
                type="text"
                className="form-input"
                value={homeAddress.address}
                onChange={(e) => setHomeAddress({ ...homeAddress, address: e.target.value })}
                placeholder="123 Main Street"
                required
              />
            </div>

            <div className="form-row form-row--3">
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={homeAddress.city}
                  onChange={(e) => setHomeAddress({ ...homeAddress, city: e.target.value })}
                  placeholder="Houston"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={homeAddress.state}
                  onChange={(e) => setHomeAddress({ ...homeAddress, state: e.target.value })}
                  placeholder="TX"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP</label>
                <input
                  type="text"
                  className="form-input"
                  value={homeAddress.zip}
                  onChange={(e) => setHomeAddress({ ...homeAddress, zip: e.target.value })}
                  placeholder="77001"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn--primary"
              disabled={savingAddress}
            >
              {savingAddress ? 'Saving...' : 'Save Address'}
            </button>
          </form>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section__header">
          <h2>QuickBooks Integration</h2>
          <p className="settings-section__desc">
            Connect your QuickBooks Online account to sync customers and invoices for bookkeeping.
          </p>
        </div>

        <div className="settings-card">
          {qbStatus.loading ? (
            <div className="settings-card__loading">
              <div className="spinner"></div>
              <span>Checking connection status...</span>
            </div>
          ) : qbStatus.connected ? (
            <div className="settings-card__content">
              <div className="qb-status qb-status--connected">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <div>
                  <strong>Connected to QuickBooks</strong>
                  <span className="qb-status__detail">Company ID: {qbStatus.companyId}</span>
                </div>
              </div>

              <div className="qb-actions">
                <button
                  className="btn btn--primary"
                  onClick={handleSyncAllCustomers}
                  disabled={syncing}
                >
                  {syncing ? 'Syncing...' : 'Sync All Customers'}
                </button>
                <button
                  className="btn btn--danger"
                  onClick={handleDisconnectQuickBooks}
                >
                  Disconnect
                </button>
              </div>

              <div className="qb-info">
                <h4>How syncing works:</h4>
                <ul>
                  <li>Click "Sync All Customers" to push all customers to QuickBooks</li>
                  <li>Only customers not already synced will be created</li>
                  <li>Individual invoices can be synced from the Invoices page</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="settings-card__content">
              <div className="qb-status qb-status--disconnected">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <div>
                  <strong>Not connected</strong>
                  <span className="qb-status__detail">Connect to sync customers and invoices</span>
                </div>
              </div>

              <button
                className="btn btn--primary"
                onClick={handleConnectQuickBooks}
              >
                Connect to QuickBooks
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Gmail Integration Section */}
      <div className="settings-section">
        <div className="settings-section__header">
          <h2>Gmail Integration</h2>
          <p className="settings-section__desc">
            Connect your Gmail account to send invoices and appointment reminders directly to customers.
          </p>
        </div>

        <div className="settings-card">
          {gmailStatus.loading ? (
            <div className="settings-card__loading">
              <div className="spinner"></div>
              <span>Checking connection status...</span>
            </div>
          ) : gmailStatus.connected ? (
            <div className="settings-card__content">
              <div className="qb-status qb-status--connected">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <div>
                  <strong>Connected to Gmail</strong>
                  <span className="qb-status__detail">Sending as: {gmailStatus.email}</span>
                </div>
              </div>

              <div className="qb-actions">
                <button
                  className="btn btn--danger"
                  onClick={handleDisconnectGmail}
                >
                  Disconnect
                </button>
              </div>

              <div className="qb-info">
                <h4>What you can do:</h4>
                <ul>
                  <li>Send invoices directly to customers via email</li>
                  <li>Emails are sent from your connected Gmail account</li>
                  <li>Invoices will be beautifully formatted with your branding</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="settings-card__content">
              <div className="qb-status qb-status--disconnected">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <div>
                  <strong>Not connected</strong>
                  <span className="qb-status__detail">Connect to send emails to customers</span>
                </div>
              </div>

              <button
                className="btn btn--primary"
                onClick={handleConnectGmail}
              >
                Connect to Gmail
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
